import { type EventRFeature } from "@eventr/common";
import { showOnlyOwnerCanDo } from "app/account/components/event-r-pro-button";
import { paywallSupported, presentUpgradePaywall } from "feat/revenueCat/upgrade";
import { useDeviceInfo } from "feat/appState/deviceInfo";
import { selectTab } from "feat/nativeNavigation/NativeRootController";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useHasFeature } from "hooks/useHasFeature";
import { useCallback, useEffect, useRef } from "react";
import { useIsOwner } from "state/hooks/useIsOwner";
import { useDisableTab } from "./tabDisabled.store";
import { TAB_BY_ID, type TabId } from "./tabs";

/** What a press on a locked tab did. */
type UpgradeOutcome = "not-owner" | "dismissed" | "bought";

/** Only a known non-owner gets the owner message; an unread user record falls through. */
async function requestUpgrade(isOwner: boolean | undefined): Promise<UpgradeOutcome> {
  if (isOwner === false) {
    showOnlyOwnerCanDo();
    return "not-owner";
  }

  return presentUpgradePaywall();
}

export interface FeatureTabLock {
  tab: TabId;
  feature: EventRFeature;
  /** SVG source for the locked glyph. */
  nativeSvg: string;
}

/**
 * Locks `tab` while the tier does not include `feature`. Builds with no paywall leave it
 * reachable.
 *
 * One caller per tab: the registration is a single global entry.
 */
export function useFeatureTabLock({ tab, feature, nativeSvg }: FeatureTabLock): void {
  const hasFeature = useHasFeature(feature);
  const isOwner = useIsOwner();
  // Subscribed, not read once: the device reports its version after the first render.
  const buildVersion = useDeviceInfo((s) => s.version.buildVersion);
  const locked = !hasFeature && paywallSupported(buildVersion);

  // Raised while a press waits on a purchase.
  const entryPending = useRef(false);

  const onSelect = useCallback(async () => {
    // Set before the await: the tier can arrive while the paywall dismisses.
    entryPending.current = true;
    if ((await requestUpgrade(isOwner)) !== "bought") entryPending.current = false;
  }, [isOwner]);

  useDisableTab(tab, locked ? { nativeSvg, onSelect: () => void onSelect() } : undefined);

  // After useDisableTab: the entry clears before selectTab runs.
  useEffect(() => {
    if (locked || !entryPending.current) return;
    entryPending.current = false;
    if (isIosNativeNavigation()) {
      void selectTab(tab);
      return;
    }
    // Keep window.pushPath: useGlobalRouter returns a fresh pushPath identity every render,
    // which would re-run this effect (and re-arm entryPending's consumer) on every render if
    // it were a dependency.
    void window.pushPath(TAB_BY_ID[tab].route);
  }, [locked, tab]);
}
